import React, { useEffect, useRef, useState } from 'react';
import { getTilePosition } from '@/utils';
import { useScene } from '@/hooks/useScene';
import { useUiStateStore } from '@/stores/uiStateStore';
import { SceneLayer } from '@/components/SceneLayer/SceneLayer';
import { RenderLayer } from '@/core/renderer/types';
import { useModelStore } from '@/stores/modelStore';

export const InlineTextEditor: React.FC = () => {
  const editingId = useUiStateStore((state) => state.editingTextBoxId);
  const editingModelItemId = useUiStateStore((state) => state.editingModelItemId);
  const close = useUiStateStore((state) => state.actions.setEditingTextBoxId);
  const closeModelItem = useUiStateStore((state) => state.actions.setEditingModelItemId);
  const modelItem = useModelStore((state) => state.items.find((item) => item.id === editingModelItemId));
  const { textBoxes, items, updateTextBox, updateModelItem } = useScene();
  const textBox = textBoxes.find((item) => item.id === editingId);
  const viewItem = items.find((item) => item.id === editingModelItemId);
  const [value, setValue] = useState('');
  const cancelled = useRef(false);

  useEffect(() => {
    setValue(textBox?.content ?? modelItem?.name ?? '');
    cancelled.current = false;
  }, [editingId, editingModelItemId, modelItem?.name, textBox?.content]);

  const isTextBox = Boolean(editingId && textBox);
  if (!isTextBox && !(editingModelItemId && modelItem && viewItem)) return null;
  const position = getTilePosition({ tile: isTextBox ? textBox!.tile : viewItem!.tile, origin: 'BOTTOM' });

  const finish = () => {
    if (!cancelled.current && value.trim()) {
      if (isTextBox) updateTextBox(textBox!.id, { content: value.trim() });
      else updateModelItem(modelItem!.id, { name: value.trim() });
    }
    close(null);
    closeModelItem(null);
  };

  return (
    <SceneLayer order={RenderLayer.TRANSFORM_CONTROLS + 2} disableAnimation>
      <input
      autoFocus
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onBlur={finish}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          cancelled.current = true;
          close(null);
          closeModelItem(null);
        }
      }}
      aria-label={isTextBox ? 'Edit text' : 'Edit label'}
      className="absolute z-[120] min-w-48 rounded-lg border-2 border-blue-500 bg-white px-3 py-2 text-sm text-slate-900 shadow-xl outline-none dark:bg-slate-900 dark:text-white"
      style={{ left: position.x, top: position.y, transform: 'translate(-50%, -50%)' }}
      />
    </SceneLayer>
  );
};
